// ==================== AXIOS API SERVICE ====================

import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getApiUrl, APP_CONSTANTS } from '../constants';
import type { ApiResponse } from '../types';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: APP_CONSTANTS.TIMEOUTS.API_REQUEST,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ==================== REQUEST INTERCEPTOR ====================
// Automatically attach auth token to every request

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// ==================== TOKEN REFRESH STATE ====================
// Prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// ==================== RESPONSE INTERCEPTOR ====================
// Handle responses and errors globally

// Endpoints that should NOT trigger token refresh on 401
const PUBLIC_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/verify-password-reset-otp',
  '/auth/resend-otp',
  '/auth/refresh',
];

const isPublicEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

api.interceptors.response.use(
  (response) => {
    // Return the data directly for successful responses
    return response;
  },
  async (error: AxiosError<ApiResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // For public endpoints (login, register, etc.), just pass through the error
    // Don't try to refresh tokens for these endpoints
    if (isPublicEndpoint(originalRequest?.url)) {
      // Extract error code from backend response (nested in error.code)
      const backendError = error.response?.data?.error as { code?: string } | undefined;
      const errorCode = typeof backendError === 'object' && backendError ? backendError.code : undefined;
      
      const errorResponse: ApiResponse = {
        success: false,
        message: error.response?.data?.message || error.message || 'An error occurred',
        error: error.response?.data?.error || error.code,
        errorCode,
        statusCode: error.response?.status || 0,
      };
      return Promise.reject(errorResponse);
    }
    
    // Handle 401 Unauthorized - Token expired (for protected endpoints only)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
      
      if (!refreshToken) {
        // No refresh token - clear auth data (don't redirect, let React handle it)
        localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.USER);
        
        const errorResponse: ApiResponse = {
          success: false,
          message: 'Session expired. Please login again.',
          statusCode: 401,
        };
        return Promise.reject(errorResponse);
      }

      // If already refreshing, wait for the new token
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post(`${getApiUrl()}/auth/refresh`, {
          refreshToken,
        });
        
        const { accessToken, refreshToken: newRefreshToken } = response.data.data || response.data;
        
        // Store new tokens
        localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        if (newRefreshToken) {
          localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
        }
        
        isRefreshing = false;
        onTokenRefreshed(accessToken);
        // Let long-lived connections (Socket.IO) re-authenticate with the new token.
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('amplify:token-refreshed'));
        
        // Retry the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        
        return api(originalRequest);
      } catch {
        isRefreshing = false;
        refreshSubscribers = [];
        
        // Refresh failed - clear tokens (don't redirect, let React Router handle it)
        localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(APP_CONSTANTS.STORAGE_KEYS.USER);
        
        const errorResponse: ApiResponse = {
          success: false,
          message: 'Session expired. Please login again.',
          statusCode: 401,
        };
        return Promise.reject(errorResponse);
      }
    }
    
    // Extract error code from backend response (nested in error.code)
    const backendError = error.response?.data?.error as { code?: string } | undefined;
    const errorCode = typeof backendError === 'object' && backendError ? backendError.code : undefined;
    
    // Format error response
    const errorResponse: ApiResponse = {
      success: false,
      message: error.response?.data?.message || error.message || 'An error occurred',
      error: error.response?.data?.error || error.code,
      errorCode,
      statusCode: error.response?.status || 0,
    };
    
    return Promise.reject(errorResponse);
  }
);

// ==================== API METHODS ====================

export const apiService = {
  // GET request
  get: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const response = await api.get<ApiResponse<T>>(url);
      return response.data;
    } catch (error) {
      // Return the error response instead of throwing
      return error as ApiResponse<T>;
    }
  },

  // POST request
  post: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await api.post<ApiResponse<T>>(url, data);
      return response.data;
    } catch (error) {
      // Return the error response instead of throwing
      return error as ApiResponse<T>;
    }
  },

  // PUT request
  put: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await api.put<ApiResponse<T>>(url, data);
      return response.data;
    } catch (error) {
      return error as ApiResponse<T>;
    }
  },

  // PATCH request
  patch: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await api.patch<ApiResponse<T>>(url, data);
      return response.data;
    } catch (error) {
      return error as ApiResponse<T>;
    }
  },

  // DELETE request
  delete: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const response = await api.delete<ApiResponse<T>>(url);
      return response.data;
    } catch (error) {
      return error as ApiResponse<T>;
    }
  },

  // Upload file with FormData
  upload: async <T>(url: string, formData: FormData, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> => {
    try {
      const response = await api.post<ApiResponse<T>>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });
      return response.data;
    } catch (error) {
      return error as ApiResponse<T>;
    }
  },
};

// Export the axios instance for advanced use cases
export { api as axiosInstance };

export default apiService;
